import React, { useMemo, useState } from 'react';
import { Aspect, modelName } from '../model';
import { HistoryRefView, MagicGenerationView } from '../history/useMagicHistory';
import { SavedAssetLink } from '../saveToAssets/types';
import { TM } from '../tokens';
import { CloseIcon, CloudLinkedIcon, DownloadIcon, LocalFileIcon, MagicIcon } from '../icons';
import { AssetThumb } from './AssetThumb';
import { SaveToAssetsButton, SaveRefLike } from './SaveToAssetsButton';
import { LightboxImage, MagicImageLightbox } from './MagicImageLightbox';
import { MagicBtn } from './ui';

// Aspect ratio → a [w, h] box for the card preview. "Auto" falls back to 4:3.
const ASPECT_BOX: Record<Aspect, [number, number]> = {
  Auto: [4, 3],
  '1:1': [1, 1],
  '3:4': [3, 4],
  '4:3': [4, 3],
  '16:9': [16, 9],
  '9:16': [9, 16],
};

// A history ref → the minimal shape SaveToAssetsButton needs (derive a target
// category + prefill the name).
function toSaveRef(ref: HistoryRefView | undefined): SaveRefLike | null {
  if (!ref) return null;
  if (ref.kind === 'asset' && ref.asset) return { kind: 'asset', asset: ref.asset };
  return { kind: 'upload', name: ref.name };
}

// The images one generation contributes to the lightbox: its result first, then
// the references that produced it. Clicking any of them opens this set at that
// image, so the preview is of the pictures themselves — the card already carries
// the record's prompt, model and settings.
function lightboxImages(item: MagicGenerationView): LightboxImage[] {
  const images: LightboxImage[] = [];
  if (item.imageUrl) {
    images.push({
      key: `result-${item.id}`,
      src: item.imageUrl,
      name: `${modelName(item.model)} · seed ${item.seed}`,
      role: 'Result',
      alpha: item.source === 'removeBg',
    });
  }
  item.refs.forEach((ref, idx) => {
    const src = ref.previewUrl ?? ref.thumbUrl;
    if (!src) return;
    images.push({
      key: ref.key,
      src,
      name: ref.name,
      role: idx === 0 ? 'Primary reference' : `Reference ${idx + 1}`,
      // Only Asset Lab refs exist in the catalog; uploads and captured frames
      // never had a node to go back to.
      assetId: ref.kind === 'asset' ? ref.asset?.id : undefined,
    });
  });
  return images;
}

// Compact relative time ("just now", "5m ago", "3d ago"), else a short date.
function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}

// Small white icon button shown on hover over the preview (download / delete).
const IconBtn: React.FC<{
  title: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, danger, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        cursor: 'pointer',
        border: 'none',
        background: hover ? (danger ? '#FDECEA' : '#fff') : 'rgba(255,255,255,0.92)',
        color: danger && hover ? TM.orange : TM.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
      }}
    >
      {children}
    </button>
  );
};

const HistoryCard: React.FC<{
  item: MagicGenerationView;
  // Opens the card's images in the lightbox, starting at the given one.
  onOpenImage: (images: LightboxImage[], index: number) => void;
  onRemixNew: () => void;
  onRemixReuse: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onReveal: () => void;
  onSaved: (link: SavedAssetLink) => void;
}> = ({ item, onOpenImage, onRemixNew, onRemixReuse, onDownload, onDelete, onReveal, onSaved }) => {
  const [hover, setHover] = useState(false);
  const [ar0, ar1] = ASPECT_BOX[item.aspect] ?? ASPECT_BOX.Auto;
  const refs = item.refs;
  const saved = item.savedAsset;
  const images = useMemo(() => lightboxImages(item), [item]);

  // Open the lightbox on the image with this key (the result, or one ref).
  const zoom = (key: string) => {
    const at = images.findIndex((img) => img.key === key);
    if (at >= 0) onOpenImage(images, at);
  };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: '#fff',
        border: `1px solid ${TM.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: hover ? '0 5px 14px rgba(12,49,105,0.10)' : 'none',
        transition: 'box-shadow .14s ease',
      }}
    >
      {/* Result preview — click to see the image itself, full size */}
      <div
        onClick={() => zoom(`result-${item.id}`)}
        title={item.imageUrl ? 'Open the image' : undefined}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${ar0} / ${ar1}`,
          maxHeight: 154,
          background: item.imageUrl
            ? `url(${item.imageUrl}) center/cover no-repeat`
            : TM.magicGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: item.imageUrl ? 'zoom-in' : 'default',
        }}
      >
        {!item.imageUrl && (
          <span
            style={{
              color: '#fff',
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            }}
          >
            seed {item.seed}
          </span>
        )}
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <span
            style={{
              background: 'rgba(45,45,45,0.85)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 3,
              letterSpacing: 0.6,
            }}
          >
            AI
          </span>
          {/* Cloud-linked (saved into Asset Lab, click to reveal) vs local/temp. */}
          {saved ? (
            <button
              type="button"
              title={`In Asset Lab: ${saved.projectName} / ${saved.categoryName} — open`}
              onClick={(e) => {
                e.stopPropagation();
                onReveal();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 18,
                padding: '0 5px',
                borderRadius: 3,
                border: 'none',
                cursor: 'pointer',
                background: 'rgba(9,172,45,0.92)',
                color: '#fff',
              }}
            >
              <CloudLinkedIcon size={11} />
            </button>
          ) : (
            <span
              title="Saved only on this device — not in Asset Lab"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 18,
                padding: '0 5px',
                borderRadius: 3,
                background: 'rgba(45,45,45,0.7)',
                color: '#fff',
              }}
            >
              <LocalFileIcon size={11} />
            </span>
          )}
        </div>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'flex',
            gap: 5,
            opacity: hover ? 1 : 0,
            transition: 'opacity .12s ease',
          }}
        >
          <SaveToAssetsButton
            variant="icon"
            imageUrl={item.imageUrl}
            // A history card's image is an object URL, so the stored Blob is the
            // only place its format shows — pass it for the prefilled filename.
            mimeType={item.imageBlob?.type}
            firstRef={toSaveRef(refs[0])}
            model={item.model}
            source={item.source}
            createdAt={item.createdAt}
            onSaved={onSaved}
          />
          <IconBtn title="Download" onClick={onDownload}>
            <DownloadIcon size={13} />
          </IconBtn>
          <IconBtn title="Delete generation" danger onClick={onDelete}>
            <CloseIcon size={12} />
          </IconBtn>
        </div>
        <span
          style={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            background: 'rgba(45,45,45,0.7)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 3,
            fontFamily: 'monospace',
          }}
        >
          {item.aspect}
          {item.resolution ? ` · ${item.resolution}` : ''}
        </span>
      </div>

      {/* Meta */}
      <div style={{ padding: '7px 9px 9px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: TM.text,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {modelName(item.model)}
          </span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              padding: '1px 5px',
              borderRadius: 3,
              background: TM.blueFaint,
              color: TM.blue,
              letterSpacing: 0.3,
              flexShrink: 0,
              maxWidth: '50%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.templateLabel || 'Custom'}
          </span>
        </div>

        <div style={{ fontSize: 10, color: TM.textFaint }}>
          seed {item.seed} · {relativeTime(item.createdAt)}
        </div>

        <div
          style={{
            padding: 6,
            borderRadius: 5,
            background: TM.bgSoft,
            fontSize: 10,
            color: TM.textMute,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 16,
          }}
        >
          {item.prompt ? (
            <>
              <b style={{ color: TM.text }}>Prompt:</b> {item.prompt}
            </>
          ) : (
            <span style={{ fontStyle: 'italic' }}>No prompt — references only.</span>
          )}
        </div>

        {/* References used — each thumbnail opens that image, not the record. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 18 }}>
          <span style={{ fontSize: 10, color: TM.textFaint, flexShrink: 0 }}>Refs</span>
          {refs.length === 0 ? (
            <span style={{ fontSize: 10, color: TM.textFaint, fontStyle: 'italic' }}>none</span>
          ) : (
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {refs.slice(0, 5).map((r, i) => (
                <button
                  type="button"
                  key={r.key}
                  title={`${r.name} — click to preview`}
                  onClick={() => zoom(r.key)}
                  style={{
                    width: 18,
                    height: 18,
                    padding: 0,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${i === 0 ? TM.blue : TM.border}`,
                    background: 'none',
                    lineHeight: 0,
                    cursor: 'zoom-in',
                    flexShrink: 0,
                  }}
                >
                  <AssetThumb src={r.thumbUrl} name={r.name} size={16} />
                </button>
              ))}
              {refs.length > 5 && (
                <span style={{ fontSize: 10, color: TM.textFaint }}>+{refs.length - 5}</span>
              )}
            </div>
          )}
        </div>

        {/* The two remix actions. Labels carry no icons at this width — the card
            is 30% narrower than it was and they would push the text to wrap. */}
        <div style={{ display: 'flex', gap: 5, marginTop: 1 }}>
          <MagicBtn
            kind="magic"
            size="sm"
            onClick={onRemixNew}
            style={{ flex: 1, justifyContent: 'center', fontSize: 11, padding: '0 6px' }}
          >
            New from result
          </MagicBtn>
          <MagicBtn
            kind="secondary"
            size="sm"
            onClick={onRemixReuse}
            style={{ flex: 1, justifyContent: 'center', fontSize: 11, padding: '0 6px' }}
          >
            Reuse setup
          </MagicBtn>
        </div>
      </div>
    </div>
  );
};

// History tab body — the generations gallery. Everything generated lands here
// automatically (with its references, prompt and settings) so the user can pick
// up any result and iterate on it: "New from result" uses the image itself as a
// reference; "Reuse setup" reloads the original refs + prompt to tweak.
export const MagicHistory: React.FC<{
  history: MagicGenerationView[];
  onRemixNew: (item: MagicGenerationView) => void;
  onRemixReuse: (item: MagicGenerationView) => void;
  onDownload: (item: MagicGenerationView) => void;
  onDelete: (id: string) => void;
  onReveal: (item: MagicGenerationView) => void;
  // Jump to a reference's own node in the catalog (offered in the lightbox for
  // references that came from Asset Lab).
  onOpenAsset: (assetId: number) => void;
  onSaved: (id: string, link: SavedAssetLink) => void;
  onGoGenerate: () => void;
}> = ({
  history,
  onRemixNew,
  onRemixReuse,
  onDownload,
  onDelete,
  onReveal,
  onOpenAsset,
  onSaved,
  onGoGenerate,
}) => {
  // The image set open in the lightbox — one card's result + its references —
  // and which of them is on screen.
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  if (history.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          background: TM.bgSoft,
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: '#fff',
            border: `1px solid ${TM.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TM.blue,
            boxShadow: TM.shadow,
          }}
        >
          <MagicIcon size={28} />
        </div>
        <div style={{ maxWidth: 360 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: TM.text }}>No generations yet</div>
          <div style={{ fontSize: 13, color: TM.textMute, lineHeight: 1.55, marginTop: 6 }}>
            Everything you generate lands here automatically — with its references, prompt and
            settings — so you can pick up any result and iterate on it.
          </div>
        </div>
        <MagicBtn kind="magic" icon={<MagicIcon size={14} />} onClick={onGoGenerate}>
          Start generating
        </MagicBtn>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        background: TM.bgSoft,
        padding: '18px 20px 28px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TM.text }}>Your generations</div>
        <div style={{ fontSize: 12, color: TM.textFaint }}>
          {history.length} saved on this device
        </div>
        <div style={{ flex: 1 }} />
        <MagicBtn kind="magic" size="sm" icon={<MagicIcon size={12} />} onClick={onGoGenerate}>
          New generation
        </MagicBtn>
      </div>
      <div
        style={{
          display: 'grid',
          // 30% denser than the original 340px / 14px so more generations fit
          // on screen at once.
          gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))',
          gap: 10,
          alignItems: 'start',
        }}
      >
        {history.map((item) => (
          <HistoryCard
            key={item.id}
            item={item}
            onOpenImage={(images, index) => setLightbox({ images, index })}
            onRemixNew={() => onRemixNew(item)}
            onRemixReuse={() => onRemixReuse(item)}
            onDownload={() => onDownload(item)}
            onDelete={() => onDelete(item.id)}
            onReveal={() => onReveal(item)}
            onSaved={(link) => onSaved(item.id, link)}
          />
        ))}
      </div>

      {lightbox && (
        <MagicImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(index) => setLightbox((prev) => prev && { ...prev, index })}
          onClose={() => setLightbox(null)}
          onOpenAsset={onOpenAsset}
        />
      )}
    </div>
  );
};
